// Notes.jsx — главная страница «Статьи и материалы».
// Шапка + PageIntro + панель действий и поиска + фильтры/сортировка/теги +
// сетка карточек статей + sidebar (топ авторов / топ статей / читают сейчас).
// На мобиле sidebar уезжает вниз, всё в одну колонку.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminService from '../../../API/AdminService';
import UserService from '../../../API/UserService';
import cl from './Notes.module.css';
import { FOLDER_PROFILE } from '../../../API/API';
import logger from '../../../utils/logger';
import PageIntro from '../../../components/PageIntro/PageIntro';
import {
    IconBook, IconSearch, IconClose, IconRefresh, IconPlus,
    IconUser, IconClock, IconCalendar, IconEye, IconStar,
    IconMessage, IconTrophy, IconChart, IconAlert,
} from '../../../components/Icons';

const Notes = () => {
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('recent');
    const [tagFilter, setTagFilter] = useState('');
    const [popularTags, setPopularTags] = useState([]);

    useEffect(() => {
        loadData();
    }, [currentPage, sortBy, tagFilter]);

    useEffect(() => {
        loadPopularTags();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [articlesResponse, statsResponse] = await Promise.all([
                AdminService.getArticles(currentPage, 20, sortBy, searchQuery, tagFilter),
                AdminService.getArticlesStats(),
            ]);

            setArticles(articlesResponse.data || []);
            setTotalPages(articlesResponse.pages || 1);
            setStats(statsResponse);
            setError('');
        } catch (err) {
            setError('Ошибка загрузки данных');
            logger.error('Ошибка загрузки статей:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadPopularTags = async () => {
        try {
            const response = await AdminService.getPopularTags(10);
            setPopularTags(response.data || []);
        } catch (err) {
            logger.error('Ошибка загрузки тегов:', err);
        }
    };

    const handleSearch = () => {
        setCurrentPage(1);
        loadData();
    };
    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') handleSearch();
    };
    const clearSearch = () => {
        setSearchQuery('');
        setCurrentPage(1);
    };

    const handleSortChange = (newSort) => {
        setSortBy(newSort);
        setCurrentPage(1);
    };
    const handleTagFilter = (tag) => {
        setTagFilter(tag === tagFilter ? '' : tag);
        setCurrentPage(1);
    };
    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo(0, 0);
    };
    const handleArticleClick = (articleId) => {
        navigate(`/main/articles/${articleId}`);
    };

    if (loading) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.loadingContainer}>
                        <div className={cl.spinner}/>
                        <p>Загрузка статей…</p>
                    </div>
                </div>
            </div>
        );
    }

    const SORT_OPTIONS = [
        { key: 'recent',     label: 'Новые' },
        { key: 'views',      label: 'Популярные' },
        { key: 'rating',     label: 'Рейтинг' },
        { key: 'difficulty', label: 'Сложность' },
    ];

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                {/* Шапка */}
                <div className={cl.pageHeaderV2}>
                    <div className={cl.pageHeaderLeft}>
                        <div className={cl.pageHeaderIcon}>
                            <IconBook width={22} height={22}/>
                        </div>
                        <div className={cl.pageHeaderText}>
                            <div className={cl.pageHeaderTopRow}>
                                <h1 className={cl.pageHeaderTitle}>Статьи и материалы</h1>
                                {stats.total_articles > 0 && (
                                    <span className={cl.pageHeaderPill}>
                                        <span className={cl.pageHeaderPillStrong}>{stats.total_articles}</span>
                                        <span className={cl.pageHeaderPillMuted}>
                                            {pluralRu(stats.total_articles, 'статья', 'статьи', 'статей')}
                                        </span>
                                    </span>
                                )}
                            </div>
                            <div className={cl.breadcrumbs}>
                                АдминПанель · Материалы · Статьи
                            </div>
                        </div>
                    </div>
                    <div className={cl.pageHeaderActions}>
                        <button onClick={loadData} className={cl.btnGhostV2} title="Обновить">
                            <IconRefresh width={15} height={15}/>
                        </button>
                        <button
                            onClick={() => navigate('/main/articles/my')}
                            className={cl.btnSecondaryV2}
                        >
                            <IconUser width={15} height={15}/>
                            <span>Мои статьи</span>
                        </button>
                        <button
                            onClick={() => navigate('/main/articles/create')}
                            className={cl.btnPrimaryV2}
                        >
                            <IconPlus width={16} height={16}/>
                            <span>Написать статью</span>
                        </button>
                    </div>
                </div>

                <PageIntro
                    storageKey="articles"
                    title="О странице «Статьи и материалы»"
                    text="Материалы по открытию центров, ведению занятий, маркетингу и развитию. Здесь и собственные публикации руководителей центров, и редакционные статьи команды СтартКласс. Можно читать, комментировать и писать свои."
                    bullets={[
                        'Сортировка — по новизне, популярности, рейтингу или сложности',
                        'Теги — клик фильтрует статьи по выбранному тегу',
                        '«Написать статью» — открывает встроенный редактор Markdown',
                        '«Мои статьи» — ваши черновики и опубликованные материалы',
                    ]}
                />

                {/* Поиск */}
                <div className={cl.searchCard}>
                    <div className={cl.searchForm}>
                        <span className={cl.searchInputIcon}>
                            <IconSearch width={18} height={18}/>
                        </span>
                        <input
                            type="text"
                            placeholder="Поиск по статьям…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={handleSearchKeyPress}
                            className={cl.searchInput}
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={clearSearch}
                                className={cl.searchClearButton}
                                aria-label="Очистить"
                            >
                                <IconClose width={13} height={13}/>
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleSearch}
                            className={cl.searchSubmitButton}
                            disabled={!searchQuery.trim()}
                        >
                            <IconSearch width={14} height={14}/>
                            <span className={cl.searchSubmitText}>Найти</span>
                        </button>
                    </div>
                </div>

                {/* Двухколоночный лейаут */}
                <div className={cl.grid}>
                    {/* Левая колонка — фильтры + статьи */}
                    <section className={cl.mainColumn}>
                        {/* Сортировка */}
                        <div className={cl.toolbar}>
                            <span className={cl.toolbarLabel}>Сортировка</span>
                            <div className={cl.sortGroup}>
                                {SORT_OPTIONS.map(opt => (
                                    <button
                                        key={opt.key}
                                        className={`${cl.sortChip} ${sortBy === opt.key ? cl.sortChipActive : ''}`}
                                        onClick={() => handleSortChange(opt.key)}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Теги */}
                        {popularTags.length > 0 && (
                            <div className={cl.toolbar}>
                                <span className={cl.toolbarLabel}>Теги</span>
                                <div className={cl.tagsList}>
                                    {popularTags.slice(0, 12).map(tag => (
                                        <button
                                            key={tag.id}
                                            className={`${cl.tagChip} ${tagFilter === tag.name ? cl.tagChipActive : ''}`}
                                            onClick={() => handleTagFilter(tag.name)}
                                        >
                                            #{tag.name}
                                            <span className={cl.tagCount}>{tag.usage_count}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Список статей */}
                        {error ? (
                            <div className={cl.errorState}>
                                <span className={cl.errorIcon}>
                                    <IconAlert width={20} height={20}/>
                                </span>
                                <h3>Не удалось загрузить статьи</h3>
                                <p>{error}</p>
                                <button onClick={loadData} className={cl.retryButton}>
                                    <IconRefresh width={14} height={14}/>
                                    <span>Попробовать снова</span>
                                </button>
                            </div>
                        ) : articles.length === 0 ? (
                            <div className={cl.emptyState}>
                                <div className={cl.emptyIconWrap}>
                                    <IconBook width={28} height={28}/>
                                </div>
                                <h2 className={cl.emptyTitle}>Статей не найдено</h2>
                                <p className={cl.emptyDescription}>
                                    Попробуйте изменить параметры поиска или создайте первую статью.
                                </p>
                                <button
                                    onClick={() => navigate('/main/articles/create')}
                                    className={cl.createFirstArticle}
                                >
                                    <IconPlus width={14} height={14}/>
                                    <span>Написать статью</span>
                                </button>
                            </div>
                        ) : (
                            <div className={cl.articlesList}>
                                {articles.map(article => (
                                    <ArticleCard
                                        key={article.id}
                                        article={article}
                                        onClick={() => handleArticleClick(article.id)}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Пагинация */}
                        {totalPages > 1 && (
                            <div className={cl.pagination}>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`${cl.pageButton} ${page === currentPage ? cl.activePage : ''}`}
                                    >
                                        {page}
                                    </button>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Sidebar */}
                    <aside className={cl.sidebar}>
                        <SidebarStats stats={stats} navigate={navigate}/>
                    </aside>
                </div>
            </div>
        </div>
    );
};


// ----- Карточка статьи -----
const ArticleCard = ({ article, onClick }) => {
    const [authorAvatar, setAuthorAvatar] = useState(null);
    const [avatarFailed, setAvatarFailed] = useState(false);

    useEffect(() => {
        const loadAuthorInfo = async () => {
            try {
                const authorInfo = await UserService.getUserById(article.author_id);
                if (authorInfo && authorInfo.data && authorInfo.data.photoLink) {
                    setAuthorAvatar(`${FOLDER_PROFILE}${authorInfo.data.photoLink}`);
                }
            } catch (err) {
                logger.error('Ошибка загрузки информации об авторе:', err);
            }
        };
        if (article.author_id) loadAuthorInfo();
    }, [article.author_id]);

    const showAvatar = authorAvatar && !avatarFailed;
    const initial = (article.author_name?.[0] || '?').toUpperCase();

    return (
        <button type="button" className={cl.articleCard} onClick={onClick}>
            <div className={cl.articleHead}>
                {showAvatar ? (
                    <img
                        src={authorAvatar}
                        alt={article.author_name}
                        className={cl.articleAvatar}
                        onError={() => setAvatarFailed(true)}
                    />
                ) : (
                    <div className={cl.articleAvatarFallback}>{initial}</div>
                )}
                <div className={cl.articleHeadInfo}>
                    <h3 className={cl.articleTitle}>{article.title}</h3>
                    <div className={cl.articleMeta}>
                        <span className={cl.metaItem}>
                            <IconUser width={11} height={11}/>
                            {article.author_name}
                        </span>
                        <span className={cl.metaItem}>
                            <IconCalendar width={11} height={11}/>
                            {AdminService.formatDate(article.created_at)}
                        </span>
                        <span className={cl.metaItem}>
                            <IconClock width={11} height={11}/>
                            {AdminService.formatReadTime(article.estimated_read_time)}
                        </span>
                        <span className={cl.metaItem}>
                            <IconChart width={11} height={11}/>
                            {AdminService.getDifficultyText(article.difficulty_level)}
                        </span>
                    </div>
                </div>
            </div>

            {article.summary && (
                <p className={cl.articleSummary}>{article.summary}</p>
            )}

            {article.tags && article.tags.length > 0 && (
                <div className={cl.articleTags}>
                    {article.tags.slice(0, 5).map(tag => (
                        <span key={tag.id} className={cl.articleTag}>
                            #{tag.name}
                        </span>
                    ))}
                    {article.tags.length > 5 && (
                        <span className={cl.moreTags}>+{article.tags.length - 5}</span>
                    )}
                </div>
            )}

            <div className={cl.articleFooter}>
                <span className={cl.footerItem}>
                    <IconEye width={11} height={11}/>
                    {AdminService.formatLargeNumber(article.views_count)}
                </span>
                <span className={cl.footerItem}>
                    <IconStar width={11} height={11}/>
                    {AdminService.formatRating(article.rating)}
                </span>
                <span className={cl.footerItem}>
                    <IconMessage width={11} height={11}/>
                    {article.comments_count}
                </span>
            </div>
        </button>
    );
};


// ----- Sidebar блоки -----
const SidebarStats = ({ stats, navigate }) => {
    return (
        <>
            {/* Топ авторов */}
            <div className={cl.sideCard}>
                <div className={cl.sideCardHead}>
                    <span className={cl.sideCardIcon}>
                        <IconTrophy width={16} height={16}/>
                    </span>
                    <h3 className={cl.sideCardTitle}>Топ авторов</h3>
                </div>
                {stats.top_authors?.length ? (
                    <div className={cl.authorsList}>
                        {stats.top_authors.slice(0, 5).map((author, index) => (
                            <div key={author.author_id} className={cl.authorItem}>
                                <div className={cl.authorRank}>#{index + 1}</div>
                                <div className={cl.authorInfo}>
                                    <div className={cl.authorName}>{author.author_name}</div>
                                    <div className={cl.authorStatsLine}>
                                        {author.articles_count} {pluralRu(author.articles_count, 'статья', 'статьи', 'статей')}
                                        <span className={cl.authorRating}>
                                            <IconStar width={10} height={10}/>
                                            {AdminService.formatRating(author.avg_rating)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={cl.emptyHint}>Пока нет авторов</div>
                )}
            </div>

            {/* Топ статей */}
            <div className={cl.sideCard}>
                <div className={cl.sideCardHead}>
                    <span className={cl.sideCardIcon}>
                        <IconChart width={16} height={16}/>
                    </span>
                    <h3 className={cl.sideCardTitle}>Топ статей</h3>
                </div>
                {stats.top_articles?.length ? (
                    <div className={cl.topArticlesList}>
                        {stats.top_articles.slice(0, 5).map(article => (
                            <button
                                key={article.id}
                                type="button"
                                className={cl.topArticleItem}
                                onClick={() => navigate(`/main/articles/${article.id}`)}
                            >
                                <div className={cl.topArticleTitle}>{article.title}</div>
                                <div className={cl.topArticleStats}>
                                    <span><IconEye width={10} height={10}/> {AdminService.formatLargeNumber(article.views_count)}</span>
                                    <span><IconStar width={10} height={10}/> {AdminService.formatRating(article.rating)}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className={cl.emptyHint}>Пока нет данных</div>
                )}
            </div>

            {/* Читают сейчас */}
            <div className={cl.sideCard}>
                <div className={cl.sideCardHead}>
                    <span className={`${cl.sideCardIcon} ${cl.sideCardIconHot}`}>
                        <IconEye width={16} height={16}/>
                    </span>
                    <h3 className={cl.sideCardTitle}>Читают сейчас</h3>
                </div>
                {stats.trending_articles?.length ? (
                    <div className={cl.topArticlesList}>
                        {stats.trending_articles.slice(0, 5).map(article => (
                            <button
                                key={article.id}
                                type="button"
                                className={cl.topArticleItem}
                                onClick={() => navigate(`/main/articles/${article.id}`)}
                            >
                                <div className={cl.topArticleTitle}>{article.title}</div>
                                <div className={cl.topArticleStats}>
                                    <span className={cl.todayViews}>
                                        +{article.today_views} сегодня
                                    </span>
                                    <span><IconStar width={10} height={10}/> {AdminService.formatRating(article.rating)}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className={cl.emptyHint}>Пока тихо</div>
                )}
            </div>
        </>
    );
};

function pluralRu(n, one, few, many) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
    return many;
}

export default Notes;
